// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { copyTextToClipboard } from './ClipboardCopyTableCell';
import {
  formatBreweryAddress,
  formatBreweryClosureStatus,
  formatBreweryCompareField,
  formatEntiteaResultJson,
  resolveEffectiveTargetType,
} from './executionCompareUtils';

function CompareField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function EntiteaJsonCopyButton({ textToCopy }) {
  const [copied, setCopied] = useState(false);
  const copyHint = copied ? 'Copied' : 'Copy EntiTea result JSON';

  const handleCopyClick = useCallback(
    async function handleCopyClick() {
      const copiedOk = await copyTextToClipboard(textToCopy);
      if (!copiedOk) {
        console.warn('Unable to copy EntiTea result to clipboard');
        return;
      }
      setCopied(true);
      window.setTimeout(function resetCopied() {
        setCopied(false);
      }, 2000);
    },
    [textToCopy],
  );

  return (
    <Tooltip title={copyHint}>
      <IconButton
        size="small"
        aria-label="Copy EntiTea result JSON to clipboard"
        onClick={handleCopyClick}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function mapBreweryDocData(docData) {
  if (!docData) {
    return null;
  }
  const brewery = {};
  for (const field in docData) {
    if (!Object.prototype.hasOwnProperty.call(docData, field)) {
      continue;
    }
    const value =
      docData[field] instanceof Timestamp ? docData[field].toDate() : docData[field];
    brewery[field] = value;
  }
  return brewery;
}

function BreweryFieldsSection({
  breweryName,
  breweryAddress,
  breweryPhone,
  breweryWebsite,
  breweryStatus,
}) {
  return (
    <Grid item xs={12} md={6}>
      <Typography variant="subtitle1" gutterBottom>
        Brewery
      </Typography>
      <Stack spacing={2}>
        <CompareField label="Name" value={breweryName} />
        <CompareField label="Address" value={breweryAddress} />
        <CompareField label="Phone" value={breweryPhone} />
        <CompareField label="Website" value={breweryWebsite} />
        <CompareField label="Status" value={breweryStatus} />
      </Stack>
    </Grid>
  );
}

function EntiteaResultSection({ formattedJson }) {
  return (
    <Grid item xs={12} md={6}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1">EntiTea result</Typography>
        {formattedJson !== '—' ? (
          <EntiteaJsonCopyButton textToCopy={formattedJson} />
        ) : null}
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          maxHeight: 420,
          overflow: 'auto',
          bgcolor: 'action.hover',
          borderRadius: 1,
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {formattedJson}
      </Box>
    </Grid>
  );
}

function BreweryComparePane({ targetId, formattedJson }) {
  const [brewery, setBrewery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(
    function loadBreweryForTarget() {
      let cancelled = false;

      const loadBrewery = async () => {
        try {
          const brewerySnap = await getDoc(doc(db, 'breweries', targetId));
          if (cancelled) {
            return;
          }
          if (!brewerySnap.exists()) {
            setFetchError('Brewery document not found.');
            return;
          }
          setBrewery(mapBreweryDocData(brewerySnap.data()));
        } catch (err) {
          if (!cancelled) {
            console.error(err);
            setFetchError(err?.message ?? 'Failed to load brewery.');
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

      loadBrewery().catch(function loadBreweryUnhandledError() {
        /* errors handled inside loadBrewery */
      });

      return function cleanupBreweryLoad() {
        cancelled = true;
      };
    },
    [targetId],
  );

  const breweryName = brewery ? formatBreweryCompareField(brewery.name) : '—';
  const breweryAddress = brewery
    ? formatBreweryAddress({
        address1: brewery.address1,
        city: brewery.city,
        state: brewery.state,
        zip: brewery.zip,
      })
    : '—';
  const breweryPhone = brewery ? formatBreweryCompareField(brewery.phone) : '—';
  const breweryWebsite = brewery ? formatBreweryCompareField(brewery.website) : '—';
  const breweryStatus = brewery
    ? formatBreweryClosureStatus({
        isOob: brewery.isOob,
        isTempClosed: brewery.isTempClosed,
        oobDate: brewery.oobDate,
      })
    : '—';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <>
      {fetchError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchError}
        </Alert>
      ) : null}
      <Grid container spacing={3}>
        <BreweryFieldsSection
          breweryName={breweryName}
          breweryAddress={breweryAddress}
          breweryPhone={breweryPhone}
          breweryWebsite={breweryWebsite}
          breweryStatus={breweryStatus}
        />
        <EntiteaResultSection formattedJson={formattedJson} />
      </Grid>
    </>
  );
}

function ExecutionCompareDialog({ open, onClose, execution }) {
  const targetId = execution?.targetId ?? '';
  const targetType = execution ? resolveEffectiveTargetType(execution) : '';
  const breweryPath =
    targetType === 'brewery' && targetId
      ? `/brewery/${encodeURIComponent(targetId)}`
      : null;
  const formattedJson = formatEntiteaResultJson(execution?.entiteaResult);
  const paneKey = execution?.id ?? targetId;

  const handleClose = useCallback(
    function handleClose() {
      onClose();
    },
    [onClose],
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Compare brewery vs EntiTea result</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1} sx={{ mb: 2 }}>
          {execution?.scheduleId ? (
            <Typography variant="body2" color="text.secondary">
              Schedule: <code>{execution.scheduleId}</code>
            </Typography>
          ) : null}
          {execution?.id ? (
            <Typography variant="body2" color="text.secondary">
              Execution: <code>{execution.id}</code>
            </Typography>
          ) : null}
          {breweryPath ? (
            <Typography variant="body2">
              <Link to={breweryPath}>Open brewery profile</Link>
            </Typography>
          ) : null}
        </Stack>

        {open && targetId ? (
          <BreweryComparePane key={paneKey} targetId={targetId} formattedJson={formattedJson} />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExecutionCompareDialog;
